import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText } from 'lucide-react';
import { getLiabilityWaiverContent, updateLiabilityWaiverContent } from '../lib/liabilityWaivers';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'sonner';

interface CustomizeLiabilityWaiverModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_LIABILITY_WAIVER_CONTENT = `ASSUMPTION OF RISK AND RELEASE OF LIABILITY WAIVER

I understand that the services provided involve certain inherent risks, dangers, and hazards that could result in serious personal injury, property damage, or death. I voluntarily assume all risks associated with these services.

I acknowledge that:
1. I am participating in these services voluntarily
2. I understand the nature of the services being provided
3. I am in good physical condition and have no medical conditions that would prevent safe participation
4. I will follow all safety instructions and guidelines provided

RELEASE AND WAIVER:
I hereby release, waive, discharge, and covenant not to sue the service provider, its employees, agents, and representatives from any and all liability, claims, demands, actions, and causes of action whatsoever arising out of or related to any loss, damage, or injury that may be sustained while participating in these services.

INDEMNIFICATION:
I agree to indemnify and hold harmless the service provider from any loss, liability, damage, or costs they may incur arising out of or related to my participation in these services.

This waiver shall be binding upon my heirs, executors, administrators, and assigns.

By signing below, I acknowledge that I have read this waiver in its entirety, understand its contents, and agree to be bound by its terms.`;

export default function CustomizeLiabilityWaiverModal({ isOpen, onClose }: CustomizeLiabilityWaiverModalProps) {
  const { selectedBusiness } = useBusiness();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalContent, setOriginalContent] = useState('');

  // Load existing liability waiver content
  const loadContent = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoading(true);
    try {
      const existingContent = await getLiabilityWaiverContent(selectedBusiness.id);
      const finalContent = existingContent || DEFAULT_LIABILITY_WAIVER_CONTENT;
      
      setContent(finalContent);
      setOriginalContent(finalContent);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading liability waiver content:', error);
      toast.error('Failed to load liability waiver content');
      setContent(DEFAULT_LIABILITY_WAIVER_CONTENT);
      setOriginalContent(DEFAULT_LIABILITY_WAIVER_CONTENT);
    } finally {
      setLoading(false);
    }
  };

  // Save liability waiver content
  const handleSave = async () => {
    if (!selectedBusiness?.id || !content.trim()) {
      toast.error('Please enter liability waiver content');
      return;
    }

    setSaving(true);
    try {
      const success = await updateLiabilityWaiverContent(selectedBusiness.id, content.trim());
      
      if (success) {
        toast.success('Liability waiver content saved successfully');
        setOriginalContent(content);
        setHasChanges(false);
      } else {
        toast.error('Failed to save liability waiver content');
      }
    } catch (error) {
      console.error('Error saving liability waiver content:', error);
      toast.error('Failed to save liability waiver content');
    } finally {
      setSaving(false);
    }
  };

  // Handle content change
  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasChanges(newContent !== originalContent);
  };

  // Reset to default content
  const handleResetToDefault = () => {
    if (confirm('Are you sure you want to reset to the default liability waiver content? Your current changes will be lost.')) {
      setContent(DEFAULT_LIABILITY_WAIVER_CONTENT);
      setHasChanges(DEFAULT_LIABILITY_WAIVER_CONTENT !== originalContent);
    }
  };

  // Handle close with unsaved changes warning
  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close without saving?')) {
        setContent(originalContent);
        setHasChanges(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadContent();
    }
  }, [isOpen, selectedBusiness?.id]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop - Higher z-index than AppSettingsModal */}
      <div 
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100002,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto'
        }}
        onClick={handleClose}
      />
      
      {/* Modal - iOS Style - Full screen on mobile (safe area for notch on iOS) */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-[100003] sm:items-center">
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-4xl sm:mx-4 sm:max-h-[90vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
          }}
        >
          {/* Header - iOS Style */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white">
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 flex items-center">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Customize Liability Waiver
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 hidden sm:block">Edit the liability waiver content that customers will see and sign</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges || !content.trim()}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors touch-manipulation ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation ml-2"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center py-12 sm:py-16">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm sm:text-base text-gray-600">Loading liability waiver content...</span>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-4 sm:space-y-5">
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                  <h3 className="text-xs sm:text-sm font-medium text-blue-900 mb-2">Editing Guidelines</h3>
                  <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                    <li>• Write your liability waiver in clear, simple language</li>
                    <li>• Include specific risks related to your services</li>
                    <li>• Consider consulting with a legal professional for your specific business needs</li>
                    <li>• The waiver will be displayed exactly as written below</li>
                  </ul>
                </div>

                {/* Content Editor */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Liability Waiver Content
                    </label>
                    <button
                      onClick={handleResetToDefault}
                      className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 underline touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Reset to Default
                    </button>
                  </div>
                  <textarea
                    value={content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    className="w-full h-64 sm:h-96 p-3 sm:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-xs sm:text-sm touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    placeholder="Enter your liability waiver content here..."
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      {content.length} characters
                    </p>
                    {hasChanges && (
                      <p className="text-xs text-amber-600 font-medium">
                        • Unsaved changes
                      </p>
                    )}
                  </div>
                </div>

                {/* Legal Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4">
                  <h3 className="text-xs sm:text-sm font-medium text-amber-900 mb-2">⚖️ Legal Disclaimer</h3>
                  <p className="text-xs sm:text-sm text-amber-800">
                    This tool is provided for convenience only. The effectiveness of any liability waiver depends on 
                    local laws and specific circumstances. We strongly recommend consulting with a qualified attorney 
                    to ensure your waiver meets legal requirements for your jurisdiction and business type.
                  </p>
                </div>

                {/* Preview */}
                <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-3">Preview</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 max-h-48 overflow-y-auto">
                    <div className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                      {content || 'No content entered...'}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    This is how your liability waiver will appear to customers
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
} 